import { ArrowBack, ArrowForward, Check, Schedule } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import { endOfDay, format, startOfDay, subDays, subMonths } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar';

const PRESETS = [
  { value: 'last1h', label: 'Last hour', shortLabel: '1H', hours: 1 },
  { value: 'last24h', label: 'Last 24 hours', shortLabel: '24H', days: 1 },
  { value: 'last7d', label: 'Last 7 days', shortLabel: '7D', days: 7 },
  { value: 'last14d', label: 'Last 14 days', shortLabel: '14D', days: 14 },
  { value: 'last30d', label: 'Last 30 days', shortLabel: '30D', days: 30 },
  { value: 'last90d', label: 'Last 90 days', shortLabel: '90D', days: 90 },
  { value: 'last6m', label: 'Last 6 months', shortLabel: '6M', months: 6 },
];

function calculateRange(preset) {
  const now = new Date();
  if (preset.hours) {
    return [new Date(now.getTime() - preset.hours * 60 * 60 * 1000), now];
  }
  if (preset.months) {
    return [startOfDay(subMonths(now, preset.months)), endOfDay(now)];
  }
  return [startOfDay(subDays(now, preset.days - 1)), endOfDay(now)];
}

/**
 * Compact time range selector with preset shortcuts and absolute date picker.
 *
 * @param {Object} props
 * @param {Date} props.startDate
 * @param {Date} props.endDate
 * @param {(startDate: Date, endDate: Date) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {'sm'|'lg'} [props.size='sm'] - sm: short label ("30D"), lg: full label ("Last 30 days")
 * @param {string[]} [props.exclude] - preset values to hide (e.g. ['last1h', 'last24h'])
 */
export default function TimeRangeSelector({
  startDate,
  endDate,
  onChange,
  disabled = false,
  size = 'sm',
  exclude = [],
  defaultPreset = 'last30d',
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(() => {
    // Try to match startDate/endDate against a preset (e.g. after page reload with URL params)
    if (startDate && endDate) {
      const available = PRESETS.filter(p => !exclude.includes(p.value));
      for (const preset of available) {
        const [presetStart, presetEnd] = calculateRange(preset);
        if (
          startDate.toDateString() === presetStart.toDateString() &&
          endDate.toDateString() === presetEnd.toDateString()
        ) {
          return preset.value;
        }
      }
    }
    return defaultPreset;
  });
  const [view, setView] = useState('presets'); // 'presets' | 'absolute'
  const [pendingRange, setPendingRange] = useState([startDate, endDate]);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback(e => {
    setAnchorEl(e.currentTarget);
    setView('presets');
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handlePresetClick = useCallback(
    preset => {
      setSelectedPreset(preset.value);
      const [start, end] = calculateRange(preset);
      onChange(start, end);
      handleClose();
    },
    [onChange, handleClose],
  );

  const handleAbsoluteDateChange = useCallback(([start, end]) => {
    setPendingRange([start, end]);
  }, []);

  const handleAbsoluteApply = useCallback(() => {
    const [start, end] = pendingRange;
    if (start && end) {
      setSelectedPreset('custom');
      onChange(start, end);
      handleClose();
    }
  }, [pendingRange, onChange, handleClose]);

  const translatedPresets = useMemo(
    () =>
      PRESETS.filter(p => !exclude.includes(p.value)).map(p => ({
        ...p,
        label: t(`dateRange.${p.value}`, { defaultValue: p.label }),
      })),
    [t, exclude],
  );

  const activePreset = translatedPresets.find(p => p.value === selectedPreset);
  const chipLabel =
    selectedPreset === 'custom'
      ? startDate && endDate
        ? `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d')}`
        : t('dateRange.custom', { defaultValue: 'Custom' })
      : size === 'lg'
        ? activePreset?.label || 'Last 30 days'
        : activePreset?.shortLabel || '30D';

  return (
    <>
      <Chip
        icon={<Schedule sx={{ fontSize: size === 'lg' ? 18 : 16 }} />}
        label={chipLabel}
        size={size === 'lg' ? 'medium' : 'small'}
        variant="outlined"
        onClick={handleOpen}
        disabled={disabled}
        sx={toolbarChipSx}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 240 } } }}
      >
        {view === 'presets' ? (
          <Box>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary' }}>
              {t('dateRange.filterTimeRange', { defaultValue: 'Filter Time Range' })}
            </Typography>
            <MenuList dense>
              {translatedPresets.map(preset => (
                <MenuItem key={preset.value} onClick={() => handlePresetClick(preset)}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {selectedPreset === preset.value ? <Check fontSize="small" /> : null}
                  </ListItemIcon>
                  <ListItemText>{preset.label}</ListItemText>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                onClick={() => {
                  setPendingRange([startDate, endDate]);
                  setView('absolute');
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }} />
                <ListItemText>{t('dateRange.absoluteDate', { defaultValue: 'Absolute date' })}</ListItemText>
                <ArrowForward fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </MenuItem>
            </MenuList>
          </Box>
        ) : (
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 'calc(100vw - 32px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Chip
                icon={<ArrowBack sx={{ fontSize: 16 }} />}
                label={t('general.back', { defaultValue: 'Back' })}
                size="small"
                variant="outlined"
                onClick={() => setView('presets')}
                sx={{ cursor: 'pointer' }}
              />
              <Typography variant="subtitle2" sx={{ ml: 1.5, color: 'text.secondary' }}>
                {t('dateRange.absoluteDate', { defaultValue: 'Absolute date' })}
              </Typography>
            </Box>
            <DateRangePicker
              value={pendingRange}
              onChange={handleAbsoluteDateChange}
              closeOnSelect={false}
              localeText={{
                start: t('general.fromDate', { defaultValue: 'From' }),
                end: t('general.toDate', { defaultValue: 'To' }),
              }}
              slots={{ field: MultiInputDateRangeField }}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
                actionBar: { actions: ['cancel', 'accept'] },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                disabled={!pendingRange[0] || !pendingRange[1]}
                onClick={handleAbsoluteApply}
              >
                {t('actions.apply', { defaultValue: 'Apply' })}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
}
